import debug from "debug";
import * as THREE from "three";

const log = debug("app:grid");

/**
 * Class representing a grid helper in a 3D scene.
 * It creates a grid and axes, updates their appearance, and responds to scene events.
 * @category Experience
 * @subcategory World
 */
class MakeGridHelper {
  /**
   * Constructs an instance of `MakeGridHelper`.
   * @param {object} experience - The experience instance containing the scene, camera, and event hub.
   */
  constructor(experience) {
    this.experience = experience;
    this.scene = experience.scene;
    this.camera = this.experience.camera;
    this.gridSize = this.gridDivisions = 500;
    this.gridY = -2.5; // Initial grid position
    this.isDarkMode = false; // Default mode

    this.gridMaker();
    this.setupEventListeners();
  }

  cleanup() {
    this.experience.eventHub.off("cameraYDirectionChanged");
    this.experience.eventHub.off("3d-enabled");
    this.experience.eventHub.off("dark-switch");

    if (this.gridHelper) {
        this.scene.remove(this.gridHelper);
        this.gridHelper = null;
    }

    this.experience = null;
    this.scene = null;
    this.camera = null;
}

  /**
   * Creates a grid and axes helpers and adds them to the scene.
   */
  gridMaker() {
    // ✅ Remove any existing axes helpers that might be in the scene
    const objectsToRemove = [];
    this.scene.traverse((object) => {
      if (object instanceof THREE.AxesHelper) {
        objectsToRemove.push(object);
      }
    });
    objectsToRemove.forEach((obj) => {
      if (obj.parent) {
        obj.parent.remove(obj);
      } else {
        this.scene.remove(obj);
      }
      // Dispose if it has geometry/material
      if (obj.geometry) obj.geometry.dispose();
      if (obj.material) {
        if (Array.isArray(obj.material)) {
          obj.material.forEach((mat) => mat.dispose());
        } else {
          obj.material.dispose();
        }
      }
    });

    const gridColor = this.getGridColor();

    // Create grid helper
    const gridHelper = new THREE.GridHelper(
      this.gridSize,
      this.gridDivisions,
      gridColor,
      gridColor
    );
    gridHelper.name = "myGrid";
    gridHelper.position.set(0.5, this.gridY, 0.5);
    this.scene.add(gridHelper);
    this.gridHelper = gridHelper;

    // Axes helper removed - was causing objects to snap and get stuck on it
    // const axesHelper = new THREE.AxesHelper(5); // Length of the axes (5 units)
    // axesHelper.name = "myGrid";
    // this.scene.add(axesHelper);
  }

  /**
   * Sets up event listeners to handle interactions and updates.
   * Listens for camera direction changes, 3D mode toggling, and dark mode switching.
   */
  setupEventListeners() {
    this.experience.eventHub.on(
      "cameraYDirectionChanged",
      (cameraYDirection) => {
        this.gridY = -2.5 * cameraYDirection; // Set gridY based on cameraYDirection
        this.updateGridPosition();
      }
    );

    this.experience.eventHub.on("3d-enabled", ({ enabled }) => {
      if (enabled) {
        this.gridHelper.visible = false; // Hide the grid from the camera
        this.gridHelper.material.transparent = true;
        this.gridHelper.material.opacity = 0;
        this.gridHelper.material.depthWrite = false; // Prevent depth conflicts
        this.gridHelper.renderOrder = -1; // Allow raycasting but not rendering
      } else {
        this.gridHelper.visible = true; // Make the grid visible again
        this.gridHelper.material.transparent = false;
        this.gridHelper.material.opacity = 1;
        this.gridHelper.material.depthWrite = true;
      }
    });

    this.experience.eventHub.on("dark-switch", ({ enabled }) => {
      this.isDarkMode = enabled;
      this.updateGridAppearance();
    });
  }

  /**
   * Updates the grid's position based on the current `gridY` value.
   */
  updateGridPosition() {
    if (this.gridHelper) {
      this.gridHelper.position.y = this.gridY;
      log("Grid position updated to", this.gridY);
    } else {
      log("Grid helper is not defined");
    }
  }

  /**
   * Updates the grid's color and opacity based on dark mode settings.
   */
  updateGridAppearance() {
    if (this.gridHelper) {
      const gridColor = this.getGridColor();
      this.gridHelper.material.color.set(gridColor);
      this.gridHelper.material.opacity = this.isDarkMode ? 1 : 1;
    }
  }

  /**
   * Determines the grid color based on the current theme mode.
   * @returns {number} - The hex color for the grid.
   */
  getGridColor() {
    return this.isDarkMode ? 0x707070 : 0xf4f4f4; // Softer, subtle grid in dark mode
  }
}

export default MakeGridHelper;
